import { Typography, IconButton, Paper, TextField} from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Delete } from '@tunasong/icons'
import { dayjs, shortUuid } from '@tunasong/models'
import type { AudioComment, Media, Persisted } from '@tunasong/schemas'
import { Popup, UserAvatar } from '@tunasong/ui-lib'
import type { FC, KeyboardEvent, MouseEvent } from 'react'
import { Fragment, useCallback, useMemo, useState } from 'react'
import invariant from 'tiny-invariant'
import { getClickPositionSeconds } from '../timeline/util.js'
import { formatTime } from '../lib/format-time.js'
import { useCurrentUser } from '@tunasong/redux'

export interface CommentStreamProps {
  playing?: boolean
  duration: number
  audioPosition: number
  height?: number
  pixelsPerSecond: number
  parent: Persisted<Media>
}

export const CommentStream: FC<CommentStreamProps> = props => {
  const { duration, pixelsPerSecond, audioPosition, playing, height = 36, parent } = props

  const { entities: storedComments } = graphHooks.useEntityChildren<AudioComment>({
    parentId: parent.id,
    filter: 'audiocomment',
  })
  const { createEntity } = graphHooks.useEntityCreate()

  const { userId } = useCurrentUser()
  invariant(userId, 'User must be logged in to comment')

  const [comments, setComments] = useState<Persisted<AudioComment>[]>([])
  const [selected, setSelected] = useState<Persisted<AudioComment> | null>(null)
  const [edit, setEdit] = useState<Persisted<AudioComment> | null>(null)
  const [editEl, setEditEl] = useState<HTMLElement | null>(null)

  const updateEntity = graphHooks.useEntityUpdate<AudioComment>({ debounceDelay: 100 })

  /** Merge comments, pri the local ones  */
  const showComments = [...comments, ...storedComments.filter(c1 => !comments.some(c2 => c1.id === c2.id))].sort(
    (a, b) => a.start - b.start
  )

  const handleInitialCreate = useCallback(
    (ev: MouseEvent) => {
      const seconds = getClickPositionSeconds(ev, { duration, pixelsPerSecond })

      const comment: Persisted<AudioComment> = {
        id: shortUuid(),
        type: 'audiocomment',
        userId,
        parentId: parent.id,
        start: seconds,
        children: [{ text: '' }],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      setComments(comments => [...comments, comment])
      setEdit(comment)
      setSelected(comment)

      void createEntity({ entity: comment, parent })
    },
    [createEntity, duration, parent, pixelsPerSecond, userId]
  )

  const onClose = useCallback(() => {
    setEditEl(null)
    setSelected(null)
    setEdit(null)
  }, [])

  const handleEventClick = useCallback(
    (comment: Persisted<AudioComment>) => (ev: MouseEvent) => {
      ev.preventDefault()
      ev.stopPropagation()
      // toggle active on click
      if (userId === comment.userId) {
        setEdit(edit => (edit === comment ? null : comment))
      }
      setSelected(selected => (selected === comment ? null : comment))
    },
    [userId]
  )

  const handleChange = useCallback(
    (comment: Persisted<AudioComment>) => (ev: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      /** Allow shift-enter to newline */
      if (ev.shiftKey || ev.key !== 'Enter') {
        return
      }
      ev.preventDefault()

      invariant(comment.id, 'Comment must have an id')

      const el = ev.target as HTMLInputElement
      const text = el.value
      const updated = { ...comment, children: [{ text }] }

      setComments(comments => [...comments.filter(c => c.id !== comment.id), updated])
      setSelected(updated)
      setEdit(null)
      updateEntity(comment.id, updated)
    },
    [updateEntity]
  )

  const handleDelete = useCallback(
    (comment: AudioComment) => (ev: MouseEvent) => {
      ev.stopPropagation()
      invariant(comment.id, 'Comment id must be set')
      updateEntity(comment.id, { trash: true })
      // update local state, set trash: true for comment.id
      setComments(comments => comments.filter(c => c.id !== comment.id))
      setSelected(null)
      setEdit(null)
    },
    [updateEntity]
  )

  /** Get the maxWidth, accounting for the next comment */
  // const getMaxWidth = useCallback(
  //   (comment: AudioComment) => {
  //     const next = showComments.find(c => c.start > comment.start)
  //     const nextStart = next?.start ?? duration
  //     const width = pixelsPerSecond * (nextStart - comment.start)
  //     return width
  //   },
  //   [duration, pixelsPerSecond, showComments]
  // )
  const active = useMemo(() => {
    // find the closest element to the current position
    if (selected || !playing) {
      return selected
    }
    let shortest = Infinity
    let active: Persisted<AudioComment> | null = null
    for (const comment of showComments) {
      const distance = Math.abs(comment.start - audioPosition)
      if (distance < shortest) {
        shortest = distance
        active = comment
      }
    }
    return active ? active : null
  }, [audioPosition, playing, selected, showComments])

  return (
    <>
      <div
        style={{
          position: 'relative',
          cursor: 'pointer',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          minHeight: active ? 80 : height,
          transition: '0.2s ease-out',
        }}
        draggable={false}
        onClick={handleInitialCreate}
        title={'Click to comment'}
      >
        {showComments.length === 0 ? (
          <Typography color="primary" variant="caption">
            Click anywhere to comment
          </Typography>
        ) : null}
        {showComments.map((comment, idx) => (
          <Fragment key={comment.id ?? idx}>
            <UserAvatar
              style={{ left: pixelsPerSecond * comment.start - 8, position: 'absolute', zIndex: 100 }}
              ref={comment === edit ? setEditEl : undefined}
              size="tiny"
              userId={comment.userId}
              link={false}
              onClick={handleEventClick(comment)}
            />

            {active && active.id === comment.id && !edit ? (
              <Paper
                style={{
                  marginLeft: 10.4,
                  marginTop: 24,
                  padding: 8,
                  borderLeft: `2px solid var(--tuna-palette-secondary-main)`,
                  borderRadius: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'absolute',
                  left: pixelsPerSecond * comment.start - 8,
                }}
              >
                <Typography variant="caption" color="primary" style={{ paddingBottom: 4 }}>
                  {active.children[0].text}
                </Typography>
                <Typography variant="caption" style={{ color: 'var(--tuna-palette-text-disabled)' }}>
                  Time: {formatTime(comment.start, { millis: false, minutePrefix: '' })}
                </Typography>
                <Typography variant="caption" style={{ color: 'var(--tuna-palette-text-disabled)' }}>
                  {dayjs(active.createdAt).format('LL')}
                </Typography>
              </Paper>
            ) : null}
          </Fragment>
        ))}
      </div>

      {edit ? (
        <Popup
          open={true}
          anchorEl={editEl}
          onClose={onClose}
          placement="right-end"
          disablePortal={false}
          clickAway={true}
        >
          <Paper
            style={{
              padding: 16,
              marginLeft: 9.6,
              backgroundColor: 'var(--tuna-palette-layers)',
              borderLeft: `2px solid var(--tuna-palette-secondary-main)`,
              borderRadius: 0,
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <TextField
              key={edit.id}
              disabled={userId !== edit.userId}
              variant="outlined"
              size="small"
              label={`Comment @ ${formatTime(edit.start, { millis: false, minutePrefix: '' })}`}
              multiline={true}
              autoFocus={true}
              onKeyDown={handleChange(edit)}
              defaultValue={edit.children[0].text}
            />
            <IconButton
              className="comment-delete-btn"
              style={{ color: 'var(--tuna-palette-action-disabled)' }}
              onClick={handleDelete(edit)}
            >
              <Delete color="inherit" />
            </IconButton>
          </Paper>
        </Popup>
      ) : null}
    </>
  )
}
